import * as THREE from 'three';

import Photograph from '../Photograph';


// global state
import Store from '../../Store';
import DATA, { TOUR_DATA } from '../../data';



const getModelWorldPosition = (modelTarget) => {
  const sceneSettings = DATA.sceneSettings;

  // Create a matrix to represent the scene's transformations
  const sceneMatrix = new THREE.Matrix4();

  // Apply the scene's position offset
  const scenePosition = new THREE.Vector3(
    sceneSettings.offsetPosition.x,
    sceneSettings.offsetPosition.y,
    sceneSettings.offsetPosition.z
  );
  sceneMatrix.setPosition(scenePosition);

  // Apply the scene's rotation offset
  const sceneRotation = new THREE.Euler(
    THREE.MathUtils.degToRad(sceneSettings.offsetRotation.x),
    THREE.MathUtils.degToRad(sceneSettings.offsetRotation.y),
    THREE.MathUtils.degToRad(sceneSettings.offsetRotation.z),
    'XYZ'
  );
  sceneMatrix.makeRotationFromEuler(sceneRotation);

  // Get the model target's position
  const modelTargetPosition = new THREE.Vector3(
    modelTarget.position[0],
    modelTarget.position[1],
    modelTarget.position[2]
  );

  // Transform the initial nav point's position from local to world coordinates
  const worldPosition = modelTargetPosition.applyMatrix4(sceneMatrix);

  return worldPosition;
};


export default class TourNavButtons {
  constructor() {

    const { 
      tourModelActiveIdx, 
      tourPointActiveIdx, 
    } = Store.getState();

    const initialTourPoint = TOUR_DATA.tourmodels[tourModelActiveIdx].tourpoints[tourPointActiveIdx];
    this.updateTourPointContent(initialTourPoint);

    // Attach event listeners
    document.getElementById("prev-button").addEventListener("click", this.handleClickPrev);
    document.getElementById("next-button").addEventListener("click", this.handleClickNext);
    
  }

  _enablePrevButton = () => {
    const prevButton = document.getElementById("prev-button");
    prevButton.classList.remove("opacity-10");
    prevButton.classList.add("opacity-80");
    prevButton.classList.add("hover:opacity-100");
    prevButton.classList.add("cursor-pointer");
    prevButton.disabled = false;
  }

  _disablePrevButton = () => {
    const prevButton = document.getElementById("prev-button");
    prevButton.classList.add("opacity-10");
    prevButton.classList.remove("opacity-80");
    prevButton.classList.remove("hover:opacity-100");
    prevButton.classList.remove("cursor-pointer");
    prevButton.disabled = true;
  }

  _nextToLastButton = () => {
    const nextButton = document.getElementById("next-button");
    nextButton.classList.add("bg-theme-100");
    nextButton.classList.add("hover:bg-theme-300");
    nextButton.classList.add("hover:border-theme-300");
    nextButton.classList.add("text-theme-800");
    nextButton.classList.add("font-sans-bold");
    nextButton.classList.add("px-16");
    nextButton.classList.remove("border-theme-100");
    nextButton.classList.remove("bg-transparent");
    nextButton.classList.remove("hover:bg-theme-800");
    nextButton.classList.remove("hover:border-theme-800");
    nextButton.classList.remove("text-white");
    nextButton.classList.remove("font-sans");
    nextButton.classList.remove("px-24");
    nextButton.textContent = "Continue Exploring";
  }

  _lastToNextButton = () => {
    const {
      tourLightMode,
    } = Store.getState();

    const nextButton = document.getElementById("next-button");
    nextButton.classList.remove("bg-theme-100");
    nextButton.classList.remove("hover:bg-theme-300");
    nextButton.classList.remove("hover:border-theme-300");
    nextButton.classList.remove("font-sans-bold");
    nextButton.classList.remove("px-16");
    nextButton.classList.add("border-theme-100");
    nextButton.classList.add("bg-transparent");
    nextButton.classList.add("hover:bg-theme-800");
    nextButton.classList.add("hover:border-theme-800");
    nextButton.classList.add("font-sans");
    nextButton.classList.add("px-24");

    if (tourLightMode) {
      nextButton.classList.remove("text-white");
      nextButton.classList.add("text-theme-800");
    } else {
      nextButton.classList.add("text-white");
      nextButton.classList.remove("text-theme-800");
    }

    nextButton.textContent = "Next";
  }

  _handleNavigate = (newTourPointIdx) => {
    const {
      app,
      tourLightMode,
      tourModelActiveIdx, 
      tourPointActiveIdx,
      isNavigating,
      currentNavPoint,
    } = Store.getState();

    // dont navigate if currently navigating between points 
    if (isNavigating) return;

    const tourPoints = TOUR_DATA.tourmodels[tourModelActiveIdx].tourpoints;
    const navPoints = DATA.navPoints;

    // set the new tour point in the store
    Store.setState({ tourPointActiveIdx: newTourPointIdx });

    // control ui button states (erg was easier in react but at least full control with threejs this way)
    if (newTourPointIdx <= 0) {
      this._disablePrevButton();
    } else {
      this._enablePrevButton();
    }
    if (newTourPointIdx >= tourPoints.length - 1) { 
      this._nextToLastButton();
    } else {
      this._lastToNextButton();
    }

    // set the nav point text content
    const newTourPoint = TOUR_DATA.tourmodels[tourModelActiveIdx].tourpoints[newTourPointIdx];
    const outgoingTourPoint = TOUR_DATA.tourmodels[tourModelActiveIdx].tourpoints[tourPointActiveIdx];


    // handle the photographs/medi
    if (outgoingTourPoint.files.length) {
      app.photograph.slideOut();
    }

    // fly in photograph/media if needed 
    if (newTourPoint.files.length) {
      app.photograph = new Photograph(newTourPoint.files[0]);
    }

    if (outgoingTourPoint.overlays) {
      app.envCube.envCubeOverlay.fadeOut();
    }

    // show the overlay
    if (newTourPoint.overlays) {
      app.envCube.envCubeOverlay.fadeIn();
    }

    // update the tour point content
    this.updateTourPointContent(newTourPoint);

    // do the navigation and view modes toggle
    if (newTourPoint.targetType === "NAVPOINT") {

      // if scene needs transition between target type
      if (tourLightMode) {
        // turn on flashlight
        app.rig.flashlight.show();

        // turn on dollhouse 
        app.dollhouse.show();

        // turn off floor markers
        app.navPoints.showFloorMarkers();

        // change ui colors for correct bg tone (sepia or black)
        this.setLightMode(false);
        app.setLightIntensity(1);

        // fade in model target in scene graph 
        app.sceneGraph.hideAllModels();
      }


      // navigate
      const targetData = navPoints.find(np => np.uuid === newTourPoint.targetUuid);
      app.cameraHandlers.handleNavigation(targetData, {
        viewMode: newTourPoint.viewMode,
        rotation: newTourPoint.rotation,
      });

    } else if (newTourPoint.targetType === "MODEL") {

      // if scene needs transition between target type
      if (!tourLightMode) {
        // turn off flashlight
        app.rig.flashlight.hide();

        // turn off dollhouse 
        app.dollhouse.hide();

        // turn off floor markers
        app.navPoints.hideFloorMarkers();

        // change ui colors for correct bg tone (sepia or black)
        this.setLightMode();
        app.setLightIntensity(1.6);
      }

      // fade in model target in scene graph 
      app.sceneGraph.hideAllModels();
      for (let i = 0; i<newTourPoint.models.length; i++) {
        app.sceneGraph.showModelById(newTourPoint.models[i]);
      }

      // new orbit target is first model in list on guided tour point 
      // Lookup the model in the scene by id and compute its world position
      const model = app.sceneGraph.getModelById(newTourPoint.models[0]);
      console.log("model", model);
      console.log("model id", newTourPoint.models[0]);

      const newOrbitTarget = new THREE.Vector3(model.position[0], model.position[1], model.position[2]);
      console.log("new orbit target", newOrbitTarget);

      // new camera position
      const newPosition = new THREE.Vector3(newTourPoint.position.x, newTourPoint.position.y, newTourPoint.position.z);

      // lerp to position
      // Use the computed world position as the orbitTarget
      app.cameraHandlers.handleNavigation(currentNavPoint, {
        viewMode: "ORBIT", 
        position: newPosition,
        orbitTarget: newOrbitTarget, 
      });

    }
  }


  handleClickNext = () => {
    const {
      tourModelActiveIdx, 
      tourPointActiveIdx, 
    } = Store.getState();

    const tourPoints = TOUR_DATA.tourmodels[tourModelActiveIdx].tourpoints;

    if (tourPointActiveIdx < tourPoints.length - 1) {
      this._handleNavigate(tourPointActiveIdx + 1);
    } else if (tourPointActiveIdx === tourPoints.length - 1) {
      window.location.href = "https://mused.org/explore";
    }
  }

  handleClickPrev = () => {
    const {
      tourModelActiveIdx, 
      tourPointActiveIdx, 
    } = Store.getState();

    if (tourPointActiveIdx > 0) {
      this._handleNavigate(tourPointActiveIdx - 1);
    }
  }

  // toggle between light text on dark bg and dark text on sepia bg
  setLightMode = (lightMode=true) => {
    const tourPointDisplay = document.getElementById("tour-ui-display");
    const tourPointContent = document.getElementById("tour-ui-content");
    const tourPointText = document.getElementById("tour-point-text");
    const nextButton = document.getElementById("next-button");
    const prevButton = document.getElementById("prev-button");

    const viewModeButton = document.getElementById("view-mode-button");
    const fullscreenButton = document.getElementById("full-screen-button");

    // set the new tour point in the store
    Store.setState({ tourLightMode: lightMode });

    if (lightMode) {
      tourPointContent.classList.add("text-theme-800");
      tourPointContent.classList.remove("text-white");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-l");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-r");
      tourPointDisplay.classList.remove("bg-gradient-to-t");
      
      tourPointText.style.textShadow = null; 

      nextButton.classList.add("border-theme-800");
      nextButton.classList.add("text-theme-800");
      nextButton.classList.remove("border-theme-100");
      nextButton.classList.remove("text-white");
      nextButton.classList.add("hover:text-white");

      prevButton.classList.add("text-theme-800");
      prevButton.classList.remove("text-white");

      viewModeButton.classList.add("text-theme-800");
      viewModeButton.classList.remove("text-white");
      viewModeButton.classList.remove("bg-black/20");
      viewModeButton.classList.remove("hover:bg-black/40");
      viewModeButton.classList.add("hover:bg-theme-800/10");
      viewModeButton.classList.add("hidden");

      fullscreenButton.classList.add("text-theme-800");
      fullscreenButton.classList.remove("text-white");
    } else {
      tourPointContent.classList.remove("text-theme-800");
      tourPointContent.classList.add("text-white");
      tourPointDisplay.classList.add("sm:bg-gradient-to-l");
      tourPointDisplay.classList.add("sm:bg-gradient-to-r");
      tourPointDisplay.classList.add("bg-gradient-to-t");
      
      tourPointText.style.textShadow = "0px 0px 5px #000"; 

      nextButton.classList.remove("border-theme-800");
      nextButton.classList.remove("text-theme-800");
      nextButton.classList.add("border-theme-100");
      nextButton.classList.add("text-white");
      nextButton.classList.add("hover:bg-theme-800");
      nextButton.classList.remove("hover:text-white");

      prevButton.classList.remove("text-theme-800");
      prevButton.classList.add("text-white");

      viewModeButton.classList.remove("text-theme-800");
      viewModeButton.classList.add("text-white");
      viewModeButton.classList.add("bg-black/20");
      viewModeButton.classList.add("hover:bg-black/40");
      viewModeButton.classList.remove("hover:bg-theme-800/10");
      viewModeButton.classList.remove("hidden");

      fullscreenButton.classList.remove("text-theme-800");
      fullscreenButton.classList.add("text-white");

    }
  }

  updateTourPointContent = (newTourPoint) => {
    const {
      tourLightMode,
    } = Store.getState();

    const tourPointDisplay = document.getElementById("tour-ui-display");
    const tourPointContent = document.getElementById("tour-ui-content");
    const tourPointTextMain = document.getElementById("tour-point-text-main");
    const tourPointTextSecondary = document.getElementById("tour-point-text-secondary");

    // update classes
    if (newTourPoint.textPosition === "center") {
      tourPointDisplay.classList.add("top-auto");
      tourPointDisplay.classList.add("right-0");
      tourPointDisplay.classList.add("left-0");
      tourPointDisplay.classList.remove("top-0");
      tourPointDisplay.classList.remove("right-auto");
      tourPointDisplay.classList.remove("left-auto");
      tourPointDisplay.classList.remove("h-full");
      tourPointDisplay.classList.remove("sm:w-1/2");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-l");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-r");
      tourPointDisplay.style.height = "50vh";


      tourPointContent.classList.add("right-auto");
      tourPointContent.classList.add("left-auto");
      tourPointContent.classList.add("sm:text-center");
      tourPointContent.classList.add("justify-center");
      tourPointContent.classList.remove("sm:right-0");
      tourPointContent.classList.remove("sm:right-auto");
      tourPointContent.classList.remove("sm:left-auto");
      tourPointContent.classList.remove("sm:left-0");
      tourPointContent.classList.remove("right-0");
      tourPointContent.classList.remove("left-0");
      tourPointContent.style.maxWidth = "none";


    } else if (newTourPoint.textPosition === "left") {
      tourPointDisplay.classList.remove("top-auto");
      tourPointDisplay.classList.remove("right-0");
      tourPointDisplay.classList.add("left-0");
      tourPointDisplay.classList.add("top-0");
      tourPointDisplay.classList.add("right-auto");
      tourPointDisplay.classList.remove("left-auto");
      tourPointDisplay.classList.add("h-full");
      tourPointDisplay.classList.add("sm:w-1/2");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-l");
      tourPointDisplay.style.height = "100vh";

      // only add the bg gradient in dark mode
      if (!tourLightMode) {
        tourPointDisplay.classList.add("sm:bg-gradient-to-r");
      }

      tourPointContent.classList.remove("right-auto");
      tourPointContent.classList.remove("left-auto");
      tourPointContent.classList.remove("sm:text-center");
      tourPointContent.classList.remove("justify-center");
      tourPointContent.classList.remove("sm:right-0");
      tourPointContent.classList.add("sm:right-auto");
      tourPointContent.classList.remove("sm:left-auto");
      tourPointContent.classList.add("sm:left-0");
      tourPointContent.classList.add("right-0");
      tourPointContent.classList.add("left-0");
      tourPointContent.style.maxWidth = "30rem";

    } else if (newTourPoint.textPosition === "right") {
      tourPointDisplay.classList.remove("top-auto");
      tourPointDisplay.classList.add("right-0");
      tourPointDisplay.classList.remove("left-0");
      tourPointDisplay.classList.add("top-0");
      tourPointDisplay.classList.remove("right-auto");
      tourPointDisplay.classList.add("left-auto");
      tourPointDisplay.classList.add("h-full");
      tourPointDisplay.classList.add("sm:w-1/2");
      tourPointDisplay.classList.remove("sm:bg-gradient-to-r");
      tourPointDisplay.style.height = "100vh";

      // only add the bg gradient in dark mode
      if (!tourLightMode) {
        tourPointDisplay.classList.add("sm:bg-gradient-to-l");
      }

      tourPointContent.classList.remove("right-auto");
      tourPointContent.classList.remove("left-auto");
      tourPointContent.classList.remove("sm:text-center");
      tourPointContent.classList.remove("justify-center");
      tourPointContent.classList.add("sm:right-0");
      tourPointContent.classList.remove("sm:right-auto");
      tourPointContent.classList.add("sm:left-auto");
      tourPointContent.classList.remove("sm:left-0");
      tourPointContent.classList.add("right-0");
      tourPointContent.classList.add("left-0");
      tourPointContent.style.maxWidth = "30rem";
    }

    // update content
    tourPointTextMain.innerHTML = newTourPoint.text.split('\n').map((paragraph) => {
        return `<p>${paragraph}</p>`;
      }).join('');

    if (newTourPoint.secondaryText && newTourPoint.secondaryText.length) {
      tourPointTextSecondary.innerHTML = newTourPoint.secondaryText;
      tourPointTextSecondary.classList.remove("hidden");
    } else {
      tourPointTextSecondary.innerHTML = "";
      tourPointTextSecondary.classList.add("hidden");
    }

  }

}